import { AfterViewInit, Component, computed, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { TransportService } from '../../services/transport.service';
import { TransportInConfigurationPresentation } from '../../models/transport-location-for-presentation';
import { CurrentUserService } from 'src/app/user/services';
import { GenericModalService } from '../../../shared/generic-modal/generic-modal.service';
import { TransportInConfiguration } from '../../models/transport-configuration';
import { PageHeaderComponent } from '../../../controls/components/advanced/page-header/page-header.component';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { StatusTrafficLightComponent } from '../../../shared/status-traffic-light/status-traffic-light.component';
import { BizUserColumnDirective } from '../../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../../shared/grid/biz-grid-date.directive';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-transport-locations-list',
    templateUrl: './transport-locations-list.component.html',
    styleUrls: ['./transport-locations-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageHeaderComponent, SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, DxTemplateModule, StatusTrafficLightComponent, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule]
})
export class TransportLocationsListComponent implements OnInit, AfterViewInit {

    locations: TransportInConfigurationPresentation[] = [];
    selectedLocations: number[] = [];
    allowAdding = false;
    allowDeleting = false;
    allowDetails = false;
    allowShowLog = false;
    canExportLocations = false;
    canUnblockLocations = false;
    hasblockedLocations = false;
    showBlockedColumn = false;
    constructor(
        public bizGridFiltervalues: BizGridFiltervalues,
        private router: Router,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private transportService: TransportService,
        private currentUserService: CurrentUserService,
        private modalService: GenericModalService,
    ) {
        this.onAddItem = this.onAddItem.bind(this);
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Transport_Action_IncomingLocation_Create');
        this.allowDetails = this.currentUserService.hasPermission('Transport_View_IncomingLocations_Details');
        this.allowDeleting = this.currentUserService.hasPermission('Transport_Action_IncomingLocation_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Transport_Action_IncomingLocation_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.locations = [];
            this.locations = await this.transportService.getTransportInConfigurationForPresentation();
            // this.locations.forEach(e => e.Enabled = (e.Enabled === undefined ? true : !!e.Enabled));
            this.hasblockedLocations = this.locations.some(e => e.BlockedByImport);
            this.showBlockedColumn = this.locations.some(e => e.BlockedByImport);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load transport locations...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onDetailsClicked(): void {
        this.navigateToDetails(this.selectedLocations);
    }

    onLogClicked(): void {
        this.navigateToLog(this.selectedLocations[0]);
    }

    onToolbarPreparing(e: any): void {
        e.toolbarOptions.items.forEach((item: any) => {
            if (item.name === 'addRowButton') {
                item.options.onClick = this.onAddItem;
            }
        });
    }

    onContextMenuPreparing(e: any): void {
        if ((e.row?.rowType === 'data') && (this.selectedLocations.length > 0)) {
            e.items = [];

            e.items.push({
                text: 'Show selected location(s)',
                onItemClick: () => this.navigateToDetails(this.selectedLocations)
            });

            e.items.push({
                text: 'Enable selected location(s)',
                onItemClick: () => this.enableMultipleLocations()
            });

            e.items.push({
                text: 'Disable selected location(s)',
                onItemClick: () => this.disableMultipleLocations()
            });
        }
    }

    onAddItem(e: any): void {
        e.cancel = true;
        this.navigateToDetails([0]);
    }

    onSelectionChanged(e: any): void {
        this.selectedLocations = e.selectedRowsData.map((item: TransportInConfigurationPresentation) => item.Id);
        this.canExportLocations = this.selectedLocations.length > 0;
        this.canUnblockLocations = this.selectedLocations.length > 0 && this.selectedLocations.every(s => this.locations.find(l => l.Id === s).BlockedByImport); //computed(() => this.locations.every(e => this.selectedLocations.includes(e.Id) && e.BlockedByImport));
    }

    onEditingStart(e: any): void {
        e.cancel = true;
        this.navigateToDetails([e.data.Id]);
    }

    onRowDblClick(e: any): void {
        if (this.allowDetails) {
            this.navigateToDetails([e.data.Id]);
        }
    }

    async onRowRemoving(e: any): Promise<void> {
        const deferred = $.Deferred();
        e.cancel = deferred.promise();

        try {
            await this.transportService.deleteTransportInConfiguration(e.data.Id);
            this.toastService.showToast('Transport location deleted...', ToastType.success);
            deferred.resolve(false);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to delete transport locations...', ToastType.error);
            deferred.resolve(true);
        }
    }

    onEnableLocations(): void {
        this.enableMultipleLocations().then(() => { });
    }

    onDisableLocations(): void {
        this.disableMultipleLocations().then(() => { });
    }

    async enableMultipleLocations() {
        const locationIds = this.selectedLocations;
        const requestText = `Do you want to enable ${locationIds.length} location(s)?`;
        await this.modalService.confirmDirect('Enable incoming transport locations', requestText).then(async () => {
            await this.transportService.setEnableDisableForInConfigurations(locationIds, true);

            this.refresh();
        }).catch(() => { });
    }

    async disableMultipleLocations() {
        const locationIds = this.selectedLocations;
        const requestText = `Do you want to disable ${locationIds.length} location(s)?`;
        await this.modalService.confirmDirect('Disable incoming transport locations', requestText).then(async () => {
            await this.transportService.setEnableDisableForInConfigurations(locationIds, false);

            this.refresh();
        }).catch(() => { });
    }

    onDuplicateLocation(e: any): void {
        const chosenRow = e as TransportInConfiguration;
        this.navigateToDetailsDuplicate(chosenRow.Id, [0]);
    }

    navigateToDetails(transportConfigurationsIds: number[]): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming/details', { transportConfigurationsIds }]);
    }

    navigateToDetailsDuplicate(duplicatetransportConfigurationsId: number, transportConfigurationsIds: number[]): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming/details', { duplicatetransportConfigurationsId, transportConfigurationsIds }]);
    }

    navigateToLog(transportConfigurationsId: number): Promise<boolean> {
        return this.router.navigate(['transport-locations/incoming/log', { transportConfigurationsId }]);
    }

    async exportLocations(locations: number[]): Promise<void> {
        await this.transportService.exportTransportInConfigurations(locations);
      }
    
      async unblockLocations(locationIds: number[]): Promise<void> {
        try {
          if (locationIds.length === 1) {
            await this.modalService.confirmDirect(`Unblock ${this.locations.find(l => l.Id === locationIds[0]).Name}`, 'Do you want to unblock this location?');
          }
          else {
            await this.modalService.confirmDirect('Unblock', `Do you want to unblock ${locationIds.length} locations?`);
          }
    
          try {
            const unblockedLocations = await this.transportService.unblockTransportInConfigurations(locationIds.sort((a, b) => a - b));
            const newLocations = this.locations.map(l => {
                const unblockedLocation = unblockedLocations.find(u => u.Id === l.Id);
                return unblockedLocation ?? l;
            });
            this.locations = newLocations;
    
            this.toastService.showToast('Locations unblocked successfully.', ToastType.success);
          }
          catch {
            this.toastService.showToast('Failed to unblock location.', ToastType.error);
          }
        }
        catch {
          // User cancelled
        }
      }
}
